import { createContext, useContext, useEffect, useRef, useCallback, ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import { queryClient } from '../lib/queryClient';
import type { RealtimeChannel, RealtimePostgresChangesPayload } from '@supabase/supabase-js';

// Map table names to TanStack Query keys that should be invalidated.
// Geen ['projects'] meer: geen enkele query hing daaronder behalve de
// dashboardlijst, en die werkt zichzelf per rij bij (useDashboardProjecten) in
// plaats van bij elke wijziging van iemand anders alle projecten te herladen.
const TABLE_QUERY_KEY_MAP: Record<string, string[][]> = {
  projects: [['uitbestedingen']],
  contracts: [['uitbestedingen']],
  leads: [['leads']],
  lead_shares: [['leads']],
  lead_appointments: [['leads']],
  contacts: [['contacts']],
  sales_invoices: [['invoices']],
  purchase_invoices: [['invoices']],
  purchase_orders: [['purchase_orders']],
  invoice_payments: [['invoices']],
  notifications: [['notifications']],
  incoming_emails: [['inbox']],
  email_assignments: [['inbox']],
  employee_tasks: [['inbox']],
  purchase_invoice_inbox: [['inbox']],
  project_appointments: [['planning']],
  project_assignments: [['planning']],
  planning_items: [['planning'], ['uitbestedingen']],
  uitbesteed_delen: [['uitbestedingen']],
  concept_clusters: [['uitbestedingen']],
  concept_cluster_leden: [['uitbestedingen']],
  // Ook ['uitbestedingen']: een onderaannemer die zijn beschikbaarheid bevestigt
  // schrijft op planning_ploegen, en dat moet de beschikbaarheidscontrole bijwerken.
  planning_ploegen: [['planning'], ['uitbestedingen']],
  planning_daglog: [['planning']],
  planning_notificaties: [['planning']],
  planning_weer: [['planning']],
  whatsapp_messages: [['whatsapp']],
  whatsapp_conversations: [['whatsapp']],
  agenda_items: [['agenda']],
  employee_availability: [['agenda']],
  rolsteiger_leveringen: [['steigers']],
  phone_calls: [['phone_calls']],
};

/** Wachttijd waarin realtime-invalidaties per tabel worden samengevoegd. */
const INVALIDATIE_DEBOUNCE_MS = 1000;

// Channel groups - each group creates ONE realtime channel for multiple tables
const CHANNEL_GROUPS: Record<string, string[]> = {
  global_crm: ['leads', 'lead_shares', 'lead_appointments', 'lead_activities', 'contacts'],
  global_projects: ['projects', 'contracts', 'quotes'],
  global_notifications: ['notifications'],
  global_invoices: ['sales_invoices', 'purchase_invoices', 'purchase_orders', 'invoice_payments'],
  global_inbox: [
    'incoming_emails',
    'email_assignments',
    'employee_tasks',
    'purchase_invoice_inbox',
  ],
  global_planning: [
    'project_appointments',
    'project_assignments',
    'planning_items',
    'planning_ploegen',
    'planning_daglog',
    'planning_notificaties',
    'planning_weer',
  ],
  global_whatsapp: ['whatsapp_messages', 'whatsapp_conversations'],
  global_agenda: ['agenda_items', 'employee_availability'],
  global_steigers: ['rolsteiger_leveringen'],
  global_telephony: ['phone_calls'],
};

// Reverse lookup: table -> channel group name
const TABLE_TO_GROUP: Record<string, string> = {};
for (const [group, tables] of Object.entries(CHANNEL_GROUPS)) {
  for (const table of tables) {
    TABLE_TO_GROUP[table] = group;
  }
}

export type RealtimeWijziging = RealtimePostgresChangesPayload<Record<string, unknown>>;
type OnUpdateCallback = (payload: RealtimeWijziging) => void;

interface RealtimeContextValue {
  subscribe: (table: string, onUpdate?: OnUpdateCallback) => () => void;
}

const RealtimeContext = createContext<RealtimeContextValue | null>(null);

export function useRealtimeContext(): RealtimeContextValue {
  const ctx = useContext(RealtimeContext);
  if (!ctx) {
    throw new Error('useRealtimeContext must be used within a RealtimeProvider');
  }
  return ctx;
}

export function RealtimeProvider({ children }: { children: ReactNode }) {
  // Track active channels
  const channelsRef = useRef<Map<string, RealtimeChannel>>(new Map());
  // Ref counts per channel group
  const refCountsRef = useRef<Map<string, number>>(new Map());
  // Callbacks per table (multiple subscribers can register callbacks)
  const callbacksRef = useRef<Map<string, Set<OnUpdateCallback>>>(new Map());
  // Invalidaties per tabel samengevoegd: een reeks wijzigingen (bulk-update,
  // iemand die snel doorklikt) geeft één refetch in plaats van één per rij.
  const invalidatieTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const createChannel = useCallback((groupName: string) => {
    const tables = CHANNEL_GROUPS[groupName];
    if (!tables) return;

    let channel = supabase.channel(groupName);

    for (const table of tables) {
      channel = channel.on(
        'postgres_changes' as never,
        { event: '*', schema: 'public', table } as never,
        (() => {
          const capturedTable = table;
          return (payload: RealtimeWijziging) => {
            // Invalidate TanStack Query keys
            const queryKeys = TABLE_QUERY_KEY_MAP[capturedTable];
            if (queryKeys) {
              const timers = invalidatieTimersRef.current;
              clearTimeout(timers.get(capturedTable));
              timers.set(
                capturedTable,
                setTimeout(() => {
                  timers.delete(capturedTable);
                  for (const key of queryKeys) {
                    queryClient.invalidateQueries({ queryKey: key });
                  }
                }, INVALIDATIE_DEBOUNCE_MS),
              );
            }

            // Call any registered callbacks for this table
            const cbs = callbacksRef.current.get(capturedTable);
            if (cbs) {
              for (const cb of cbs) {
                try {
                  cb(payload);
                } catch (e) {
                  console.error('Realtime callback error:', e);
                }
              }
            }
          };
        })(),
      );
    }

    channel.subscribe();
    channelsRef.current.set(groupName, channel);
  }, []);

  const removeChannel = useCallback((groupName: string) => {
    const channel = channelsRef.current.get(groupName);
    if (channel) {
      supabase.removeChannel(channel);
      channelsRef.current.delete(groupName);
    }
  }, []);

  const subscribe = useCallback(
    (table: string, onUpdate?: OnUpdateCallback) => {
      const groupName = TABLE_TO_GROUP[table];
      if (!groupName) {
        console.warn(`[RealtimeContext] No channel group found for table "${table}"`);
        return () => {};
      }

      // Register callback if provided
      if (onUpdate) {
        if (!callbacksRef.current.has(table)) {
          callbacksRef.current.set(table, new Set());
        }
        callbacksRef.current.get(table)!.add(onUpdate);
      }

      // Increment ref count
      const currentCount = refCountsRef.current.get(groupName) || 0;
      refCountsRef.current.set(groupName, currentCount + 1);

      // Create channel if this is the first subscriber
      if (currentCount === 0) {
        createChannel(groupName);
      }

      // Return unsubscribe function
      return () => {
        // Remove callback
        if (onUpdate) {
          const cbs = callbacksRef.current.get(table);
          if (cbs) {
            cbs.delete(onUpdate);
            if (cbs.size === 0) {
              callbacksRef.current.delete(table);
            }
          }
        }

        // Decrement ref count
        const count = refCountsRef.current.get(groupName) || 0;
        const newCount = Math.max(0, count - 1);
        refCountsRef.current.set(groupName, newCount);

        // Remove channel if no more subscribers
        if (newCount === 0) {
          removeChannel(groupName);
        }
      };
    },
    [createChannel, removeChannel],
  );

  // Cleanup all channels on unmount
  useEffect(() => {
    const invalidatieTimers = invalidatieTimersRef.current;
    return () => {
      for (const [groupName] of channelsRef.current) {
        const channel = channelsRef.current.get(groupName);
        if (channel) {
          supabase.removeChannel(channel);
        }
      }
      channelsRef.current.clear();
      refCountsRef.current.clear();
      for (const timer of invalidatieTimers.values()) clearTimeout(timer);
      invalidatieTimers.clear();
      callbacksRef.current.clear();
    };
  }, []);

  return <RealtimeContext.Provider value={{ subscribe }}>{children}</RealtimeContext.Provider>;
}
